import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';

export default function ConviteParticipante() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();

  const [validando, setValidando] = useState(true);
  const [conviteValido, setConviteValido] = useState(false);
  const [perfilConvite, setPerfilConvite] = useState('');
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);

  const [dados, setDados] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    cargo: '',
    senha: '',
    confirmarSenha: ''
  });

  useEffect(() => {
    let ativo = true;

    async function validarConvite() {
      try {
        const response = await fetch(
          `/api/convites/${encodeURIComponent(token)}`,
          {
            method: 'GET',
            credentials: 'include'
          }
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok || !data.ok) {
          throw new Error(
            data.error || 'Este convite não é válido.'
          );
        }

        const perfil = data.convite?.perfil;

        if (!['participante', 'coach'].includes(perfil)) {
          throw new Error(
            'Este convite possui um perfil de acesso inválido.'
          );
        }

        if (ativo) {
          setPerfilConvite(perfil);
          setConviteValido(true);
        }
      } catch (e) {
        if (ativo) {
          setErro(
            e.message || 'Não foi possível validar o convite.'
          );
          setConviteValido(false);
        }
      } finally {
        if (ativo) {
          setValidando(false);
        }
      }
    }

    validarConvite();

    return () => {
      ativo = false;
    };
  }, [token]);

  function atualizar(campo, valor) {
    setDados(prev => ({
      ...prev,
      [campo]: valor
    }));
  }

  async function criarAcesso() {
    if (carregando) return;

    const cadastro = {
      nome: dados.nome.trim(),
      email: dados.email.trim().toLowerCase(),
      telefone: dados.telefone.trim(),
      empresa: dados.empresa.trim(),
      cargo: dados.cargo.trim()
    };

    if (!cadastro.nome) {
      setErro('Informe seu nome completo.');
      return;
    }

    if (!cadastro.email) {
      setErro('Informe seu e-mail.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cadastro.email)) {
      setErro('Informe um e-mail válido.');
      return;
    }

    if (!dados.senha || dados.senha.length < 8) {
      setErro('A senha deve ter pelo menos 8 caracteres.');
      return;
    }

    if (dados.senha !== dados.confirmarSenha) {
      setErro('A confirmação da senha não corresponde.');
      return;
    }

    setErro('');
    setCarregando(true);

    try {
      const ativacaoResponse = await fetch(
        `/api/convites/${encodeURIComponent(token)}/ativar`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...cadastro,
            senha: dados.senha
          })
        }
      );

      const ativacao = await ativacaoResponse
        .json()
        .catch(() => ({}));

      if (!ativacaoResponse.ok || !ativacao.ok) {
        if (ativacao.code === 'CONTA_JA_EXISTE') {
          throw new Error(
            'Já existe uma conta com este e-mail. Entre com sua conta para continuar.'
          );
        }

        throw new Error(
          ativacao.error || 'Não foi possível ativar seu acesso.'
        );
      }

      await refreshAuth();

      if (perfilConvite === 'coach') {
        navigate('/coach', {
          replace: true
        });
        return;
      }

      const participanteId = ativacao.participante?.id;

      if (!participanteId) {
        throw new Error(
          'O servidor não retornou o Participante criado.'
        );
      }

      const sessaoResponse = await fetch('/api/sessions/start', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          participant_id: participanteId,
          meta: {
            origem: 'convite_participante',
            versao_frontend: '2026-08-30'
          }
        })
      });

      const sessao = await sessaoResponse
        .json()
        .catch(() => ({}));

      if (!sessaoResponse.ok || !sessao.ok) {
        throw new Error(
          sessao.error || 'Não foi possível iniciar a avaliação.'
        );
      }

      const sessionId = sessao.session?.id;

      if (!sessionId) {
        throw new Error(
          'O servidor não retornou a sessão da avaliação.'
        );
      }

      navigate('/icv', {
        replace: true,
        state: {
          acessoPorConvite: true,
          sessionId,
          participante: ativacao.participante
        }
      });
    } catch (e) {
      setErro(
        e.message ||
          (
            perfilConvite === 'coach'
              ? 'Não foi possível criar seu acesso.'
              : 'Não foi possível iniciar a avaliação.'
          )
      );
    } finally {
      setCarregando(false);
    }
  }

  const ehCoach = perfilConvite === 'coach';

  const containerStyle = {
    width: '100%',
    maxWidth: 640,
    margin: '2rem auto',
    padding: '2rem 1.5rem',
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 2px 10px rgba(36,47,60,0.10)',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: 6,
    fontWeight: 600,
    color: '#242F3C'
  };

  const inputStyle = {
    width: '100%',
    padding: '11px 12px',
    border: '1px solid #d8dde3',
    borderRadius: 7,
    boxSizing: 'border-box',
    fontSize: 15,
    background: '#fff'
  };

  if (validando) {
    return (
      <div style={containerStyle}>
        Validando convite...
      </div>
    );
  }

  if (!conviteValido) {
    return (
      <div style={containerStyle}>
        <h2 style={{ color: '#242F3C' }}>
          Convite indisponível
        </h2>

        <div
          role="alert"
          style={{
            marginTop: 16,
            padding: '12px',
            borderRadius: 7,
            background: '#FFF3F2',
            color: '#B42318',
            border: '1px solid #FECDCA',
            fontWeight: 600
          }}
        >
          {erro || 'Este convite não pode mais ser utilizado.'}
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div
        style={{
          background: '#F5FAFF',
          borderRadius: 10,
          padding: '1.5rem',
          marginBottom: '2rem',
          border: '1px solid #E6EDF3'
        }}
      >
        <h2
          style={{
            marginBottom: '0.5rem',
            fontWeight: 700,
            color: '#242F3C'
          }}
        >
          {ehCoach
            ? 'Acesso profissional ao ICV'
            : 'Inventário de Competências para Vendas'}
        </h2>

        <p
          style={{
            fontSize: 15,
            color: '#222',
            lineHeight: 1.5
          }}
        >
          {ehCoach
            ? 'Preencha seus dados e crie sua senha para acessar a Área do Coach / Mentor / Psicólogo.'
            : 'Preencha seus dados e crie sua senha de acesso para iniciar o inventário.'}
        </p>
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        <div>
          <label style={labelStyle}>Nome completo *</label>
          <input
            type="text"
            autoComplete="name"
            value={dados.nome}
            onChange={e => atualizar('nome', e.target.value)}
            style={inputStyle}
            disabled={carregando}
          />
        </div>

        <div>
          <label style={labelStyle}>E-mail *</label>
          <input
            type="email"
            autoComplete="email"
            value={dados.email}
            onChange={e => atualizar('email', e.target.value)}
            style={inputStyle}
            disabled={carregando}
          />
        </div>

        <div>
          <label style={labelStyle}>Telefone</label>
          <input
            type="tel"
            autoComplete="tel"
            value={dados.telefone}
            onChange={e => atualizar('telefone', e.target.value)}
            style={inputStyle}
            disabled={carregando}
          />
        </div>

        <div>
          <label style={labelStyle}>Empresa</label>
          <input
            type="text"
            autoComplete="organization"
            value={dados.empresa}
            onChange={e => atualizar('empresa', e.target.value)}
            style={inputStyle}
            disabled={carregando}
          />
        </div>

        <div>
          <label style={labelStyle}>Cargo</label>
          <input
            type="text"
            autoComplete="organization-title"
            value={dados.cargo}
            onChange={e => atualizar('cargo', e.target.value)}
            style={inputStyle}
            disabled={carregando}
          />
        </div>

        <div
          style={{
            marginTop: 8,
            paddingTop: 20,
            borderTop: '1px solid #E6EDF3'
          }}
        >
          <h3
            style={{
              margin: '0 0 16px 0',
              color: '#242F3C'
            }}
          >
            Crie sua senha de acesso
          </h3>

          <label style={labelStyle}>Senha *</label>
          <input
            type="password"
            autoComplete="new-password"
            value={dados.senha}
            onChange={e => atualizar('senha', e.target.value)}
            style={inputStyle}
            disabled={carregando}
          />
        </div>

        <div>
          <label style={labelStyle}>Confirmar senha *</label>
          <input
            type="password"
            autoComplete="new-password"
            value={dados.confirmarSenha}
            onChange={e =>
              atualizar('confirmarSenha', e.target.value)
            }
            style={inputStyle}
            disabled={carregando}
          />
        </div>
      </div>

      {erro && (
        <div
          role="alert"
          style={{
            marginTop: 16,
            padding: '10px 12px',
            borderRadius: 7,
            background: '#FFF3F2',
            color: '#B42318',
            border: '1px solid #FECDCA',
            fontWeight: 600
          }}
        >
          {erro}
        </div>
      )}

      <button
        type="button"
        onClick={criarAcesso}
        disabled={carregando}
        style={{
          padding: '0.7rem 2.5rem',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          fontWeight: 600,
          cursor: carregando ? 'default' : 'pointer',
          background: carregando ? '#999' : '#FF6A00',
          marginTop: 24
        }}
      >
        {carregando
          ? 'Criando acesso...'
          : ehCoach
            ? 'Criar acesso'
            : 'Criar acesso e iniciar avaliação'}
      </button>
    </div>
  );
}
